import React, { useMemo } from "react";
import { Button, CircularProgress, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { useMutation } from "@apollo/client";

import AppSelectField from "../apps/AppSelectField";
import { AppState, dispatch, useSelector } from "../../store/store";
import { profileActions } from "../../store/slices/profileSlice";
import AppRadio from "../apps/AppRadio";
import { RadioProps } from "../../types/components/Form";
import { IdCardSvg } from "../../assets/svg/IdCardSvg";
import AppUploadSingle from "../apps/AppUpload/UploadSingle";
import { UPLOAD_IDENTITY } from "../../graphql/mutations/profile";
import { showErrorToast } from "../apps/AppError";
import { useQueryProfile } from "../../graphql/apollo/useQuery";
import { DOCUMENT_TYPE } from "../../constants/constants";

interface FormProps {
  handleClose: () => void;
}

const UploadIdentityForm: React.FC<FormProps> = ({ handleClose }) => {
  const { t } = useTranslation();
  const { refetch } = useQueryProfile();
  const {
    documentType,
    showUpload,
    uploadDocument,
    uploadFrontDocument,
    uploadBackDocument,
    issueCountry,
  } = useSelector<AppState["profile"]>(({ profile }) => profile);
  const options = useMemo(() => countryList().getData(), []);
  const documentTypes: RadioProps[] = [
    { value: "passport", label: t("passport") },
    { value: "drivingLicence", label: t("drivingLicence") },
    { value: "idCard", label: t("idCard") },
    { value: "residencePermit", label: t("residencePermit") },
  ];

  const handleChangeType = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch(profileActions.selectDocumentType(e.target.value));

  const setUploadedFile = (uri: File) =>
    dispatch(profileActions.setUploadDocumentFile({ file: uri }));
  const setPreview = (uri: File) =>
    dispatch(profileActions.setUploadDocumentPreview({ preview: uri }));
  const setUploadedFrontFile = (uri: File) =>
    dispatch(profileActions.setUploadFrontDocumentFile({ file: uri }));
  const setPreviewFront = (uri: File) =>
    dispatch(profileActions.setUploadFrontDocumentPreview({ preview: uri }));
  const setUploadedBackFile = (uri: File) =>
    dispatch(profileActions.setUploadBackDocumentFile({ file: uri }));
  const setPreviewBack = (uri: File) =>
    dispatch(profileActions.setUploadBackDocumentPreview({ preview: uri }));
  const handleNext = () => dispatch(profileActions.handleShowUpload());

  const [uploadIdentityDoc, { loading }] = useMutation(UPLOAD_IDENTITY, {
    onCompleted: (data) => {
      if (data.uploadIdentityDoc) {
        refetch();
        dispatch(profileActions.toggleUploadDialog());
        handleClose();
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const handleSubmit = (data: any) => {
    uploadIdentityDoc({
      variables: {
        input: {
          documentType,
          issueCountry,
          imageFile: uploadDocument.file,
          frontFile: uploadFrontDocument.file,
          backFile: uploadBackDocument.file,
        },
      },
    });
  };

  return (
    <div
      style={{
        paddingBottom: 200,
        overflowY: "scroll",
        overflowX: "clip",
        height: "100vh",
        width: "100wv",
      }}
    >
      {showUpload ? (
        <>
          <IdCardSvg style={{ marginLeft: "auto", marginRight: "auto" }} />
          <Typography
            variant="body2"
            sx={{ textAlign: "center", mt: 4, mb: 1 }}
          >
            {t("uploadInfo")}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {t("uploadDescription")}
          </Typography>
          {documentType === DOCUMENT_TYPE.ID_CARD ? (
            <>
              <AppUploadSingle
                setUploadedFile={setUploadedFrontFile}
                setPreview={setPreviewFront}
                preview={uploadFrontDocument.preview}
                uploadText={t("chooseFileFront")}
              />
              <AppUploadSingle
                setUploadedFile={setUploadedBackFile}
                setPreview={setPreviewBack}
                preview={uploadBackDocument.preview}
                uploadText={t("chooseFileBack")}
              />
            </>
          ) : (
            <>
              <AppUploadSingle
                setUploadedFile={setUploadedFile}
                setPreview={setPreview}
                preview={uploadDocument.preview}
                uploadText={t("chooseFile")}
              />
            </>
          )}

          <Button
            sx={{ padding: 1, marginLeft: 2, marginRight: 2, width: "90%" }}
            variant="contained"
            color="warning"
            onClick={handleSubmit}
            fullWidth
            disabled={documentType === ""}
          >
            {loading ? (
              <CircularProgress color="inherit" />
            ) : (
              t("uploadDocuments")
            )}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
            {t("chooseCountry")}
          </Typography>
          <Divider />
          <AppSelectField
            options={options}
            name="country"
            defaultValue={issueCountry}
            onChange={(e) => {
              dispatch(profileActions.selectDocumentType(e));
            }}
          />
          <Typography variant="body1" sx={{ textAlign: "center", mt: 3 }}>
            {t("selectDocumentType")}
          </Typography>
          <Typography variant="caption" sx={{ textAlign: "center" }}>
            {t("useValid")}
          </Typography>
          <AppRadio
            isRow={false}
            value={documentType}
            onChange={(e: any) => handleChangeType(e)}
            radios={documentTypes}
          />
          <Button
            variant="contained"
            color="warning"
            onClick={handleNext}
            fullWidth
            disabled={documentType === ""}
          >
            {t("continue")}
          </Button>
        </>
      )}
    </div>
  );
};

export default UploadIdentityForm;
